import { util, type Canvas } from 'fabric';
import { loadFontVariant } from '@/utils/fontLoader';
import type { HistorySnapshot } from './CanvasHistory';
import { isLayerLocked, reorderLayers, setLayerLocked, type LayerObject, type LayerSourceRef } from './CanvasLayers';

// What the backend stores per canvas. width/height/bg_color/template_id are its own columns (lists and
// search use them); everything else the editor needs to rebuild the canvas goes in `structure`.
//
// structure.layers is the fabric serialization of each user object, back to front, so the array order
// is the layer order. Images reference their template/SKU through `sourceRef` (by id) and keep `src` as
// a cache, so opening a canvas needs no request per element. Texts keep `fontUrl` to load their font.

export const CANVAS_DOCUMENT_VERSION = 1;

// Canvas settings that aren't backend columns. The editor helpers (grid, safe zone, snap to grid) are
// local to each editing session and never saved.
export interface CanvasSettings {
  sizeId: string;
}

export type CanvasLayerData = Record<string, unknown> & {
  type?: string;
  sourceRef?: LayerSourceRef;
  fontFamily?: string;
  fontUrl?: string;
};

export interface CanvasStructure {
  version: typeof CANVAS_DOCUMENT_VERSION;
  settings: CanvasSettings;
  layers: CanvasLayerData[];
}

export interface CanvasSaveFields {
  structure: CanvasStructure;
  bg_color: string;
  width: number;
  height: number;
  template_id: number | null;
}

export type CanvasSaveField = keyof CanvasSaveFields;
export type SerializedSaveFields = Record<CanvasSaveField, string>;

const SAVE_FIELDS: CanvasSaveField[] = ['structure', 'bg_color', 'width', 'height', 'template_id'];

// 2 decimals are far below a visible pixel and cut the size of transforms like 0.333333333.
const roundNumbers = (value: unknown): unknown => {
  if (typeof value === 'number') return Number.isInteger(value) ? value : Math.round(value * 100) / 100;
  if (Array.isArray(value)) return value.map(roundNumbers);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, roundNumbers(item)]));
  }
  return value;
};

// The backend only takes #RRGGBB.
export const normalizeHexColor = (color: string): string => {
  const match = /^#?([\da-f]{3}|[\da-f]{6})$/i.exec(color.trim());
  if (!match) return '#FFFFFF';
  const hex = match[1].length === 3 ? [...match[1]].map((c) => c + c).join('') : match[1];
  return `#${hex.toUpperCase()}`;
};

const toTemplateId = (layers: CanvasLayerData[]): number | null => {
  const id = layers.find((layer) => layer.sourceRef?.kind === 'template')?.sourceRef?.id;
  const numericId = Number(id);
  return id !== undefined && Number.isInteger(numericId) ? numericId : null;
};

export const buildSaveFields = (snapshot: HistorySnapshot): CanvasSaveFields => {
  const layers = snapshot.objects.map((object) => roundNumbers(object) as CanvasLayerData);
  return {
    structure: {
      version: CANVAS_DOCUMENT_VERSION,
      settings: { sizeId: snapshot.canvasSizeId },
      layers,
    },
    bg_color: normalizeHexColor(snapshot.backgroundColor),
    width: snapshot.canvasWidth,
    height: snapshot.canvasHeight,
    template_id: toTemplateId(layers),
  };
};

// One string per field: comparing them tells which fields changed without deep comparisons.
export const serializeSaveFields = (fields: CanvasSaveFields): SerializedSaveFields =>
  Object.fromEntries(SAVE_FIELDS.map((field) => [field, JSON.stringify(fields[field])])) as SerializedSaveFields;

export const getChangedFields = (current: SerializedSaveFields, saved: SerializedSaveFields | null): CanvasSaveField[] =>
  SAVE_FIELDS.filter((field) => !saved || current[field] !== saved[field]);

const isObject = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);

// Anything that isn't a v1 document (e.g. the `{}` of a newly created canvas) opens as a blank canvas.
export const parseCanvasStructure = (value: unknown): CanvasStructure | null => {
  if (!isObject(value) || value.version !== CANVAS_DOCUMENT_VERSION || !Array.isArray(value.layers)) return null;
  const settings = isObject(value.settings) ? value.settings : {};
  return {
    version: CANVAS_DOCUMENT_VERSION,
    settings: {
      sizeId: typeof settings.sizeId === 'string' ? settings.sizeId : '',
    },
    layers: value.layers.filter(isObject) as CanvasLayerData[],
  };
};

const loadLayerFonts = (layers: CanvasLayerData[]) => {
  const fonts = new Map<string, string>();
  layers.forEach(({ fontFamily, fontUrl }) => {
    if (fontFamily && fontUrl) fonts.set(fontFamily, fontUrl);
  });
  // A font that fails to load leaves its text in the fallback font instead of blocking the canvas.
  return Promise.allSettled(
    [...fonts].map(([id, url]) => loadFontVariant({ id, url, name: id, file: '' }))
  );
};

// Rebuilds the saved layers on the canvas, keeping their order. Fonts load first so texts are measured
// with them. Layers are enlivened one by one: an image that no longer loads is skipped instead of
// failing the whole canvas. Resolves with how many layers couldn't be loaded.
export const loadCanvasLayers = async (canvas: Canvas, layers: CanvasLayerData[]): Promise<number> => {
  await loadLayerFonts(layers);
  const results = await Promise.allSettled(
    layers.map(async (layer) => ((await util.enlivenObjects([layer])) as LayerObject[])[0])
  );
  if (canvas.disposed) return 0;

  const objects = results.flatMap((result) => (result.status === 'fulfilled' && result.value ? [result.value] : []));
  // Documents saved before locks covered resizing and text editing only kept the movement flags.
  objects.forEach((object) => {
    if (isLayerLocked(object)) setLayerLocked(object, true);
    canvas.add(object);
  });
  reorderLayers(canvas, objects);
  return layers.length - objects.length;
};
